import { useQuery } from '@tanstack/react-query';

import CompanyInfoService from '../services/CompanyInfoService';
import type { CompanyInfoResponse } from '../types/api/response/companyInfo';
import type { DivisionResponse } from '../types/api/response/division';

export function companyInfoQueryKey(companyId?: string) {
  return ['CompanyInfoService.getCompanyInfo', companyId];
}

/**
 * Source unique des données entreprise (company, divisions, employees, roles, onBoardings,
 * variablePayroll) via un seul appel réseau GET /v2/company/{companyId}/info.
 * Les hooks historiques useCompany / useFetchDivisions / useFetchUsers sont des façades dessus :
 * même queryKey, donc une seule requête partagée quelle que soit la page.
 * <p>
 * Pas de staleTime : beaucoup de mutations de l'app n'invalident aucun cache et comptent sur le
 * refetch-on-mount pour la fraîcheur à chaque navigation — un staleTime > 0 fige les données
 * jusqu'à un rechargement complet de la page.
 */
export default function useCompanyInfo(companyId?: string) {
  return useQuery({
    enabled: !!companyId,
    queryKey: companyInfoQueryKey(companyId),
    queryFn: () => CompanyInfoService.getCompanyInfo(companyId!),
  });
}

/**
 * Reconstruit la forme « full data » historique de GET /v2/division?companyId= à partir des blocs
 * bruts de la réponse consolidée : nbUsers (employés valides de l'UT), n1User/n2User/assistantUser
 * (résolus depuis employees[]). numberLeavesOnDay/numberLeavesOnWeek ne sont pas dérivables et
 * restent absents — aucun consommateur ne les lit.
 */
export function makeFullDataDivisions(
  info: CompanyInfoResponse | undefined
): DivisionResponse[] {
  if (!info) {
    return [];
  }
  const employeesById = new Map(
    info.employees.map((employee) => [employee.id, employee])
  );
  return info.divisions.map((division) => ({
    ...division,
    nbUsers: info.employees.filter(
      (employee) => employee.divisionId === division.id && employee.valid
    ).length,
    n1User: division.n1 ? employeesById.get(division.n1) : undefined,
    n2User: division.n2 ? employeesById.get(division.n2) : undefined,
    assistantUser: division.assistant
      ? employeesById.get(division.assistant)
      : undefined,
  }));
}
